let route = null;
import { inject } from "vue";
import { useAssetTransforms } from "@/Composables";

export default function () {
    route = route ? route : inject("route");
    const { marketingAssetUrl } = useAssetTransforms();

    const jsonLdMainEntityBlogIndex = (articles, categories) => {
        let hasPart = [];
        let tags = [];

        articles.data.forEach((article, index) => {
            hasPart.push({
                "@type": "ListItem",
                position: index + 1,
                name: article.title,
                url: route("web.education.show", article.slug),
            });
        });

        categories.forEach((category, index) => {
            tags.push({
                "@type": "ListItem",
                position: index + 1,
                name: category.name,
                description: category.description || `Articles about ${category.name}`,
                item: {
                    "@type": "Thing",
                    "@id": route("web.education.category.show", category.slug),
                    name: category.name,
                },
            });
        });

        return {
            "@type": "CollectionPage",
            "@id": route(route().current(), route().params),
            name: "Blog Articles",
            description: "A paginated index of blog articles",
            mainEntity: {
                "@type": "ItemList",
                name:
                    "Page " + articles.current_page + " of Blog Articles",
                url: route(route().current(), route().params),
                numberOfItems: articles.total,
                itemListElement: hasPart,
            },
            mentions: tags,
        };
    };

    const jsonLdMainEntityBlogShow = (article) => {
        let mentions = [];

        article.categories?.forEach((tag, index) => {
            mentions.push({
                "@type": "ListItem",
                position: index + 1,
                item: {
                    "@id": route("web.education.category.show", tag.slug),
                    name: tag.name,
                },
            });
        });

        let mainEntity = {
            "@type": "Article",
            "@id": route("web.education.show", article.slug),
            name: article.title,
            headline: article.title,
            description: article.headline,
            datePublished: article.published_at,
            dateModified: article.updated_at,
            author: {
                "@type": "Organization",
                name: "SECLOCK",
            },
        };

        if (article.image) {
            mainEntity.image = marketingAssetUrl(article.image);
        }

        if (mentions.length) {
            mainEntity.mentions = mentions;
        }
        return mainEntity;
    };

    const jsonLdMainEntityManufacturers = (manufacturers) => {
        const maxItems = 20;
        let itemListElement = [];
        const totalCount = manufacturers?.length || 0;

        manufacturers.slice(0, maxItems).forEach((manufacturer, index) => {
            const manufacturerName = manufacturer.display_name || manufacturer.name || 'Unknown Manufacturer';
            let productTypeArray = [];

            if(manufacturer.tags && manufacturer.tags['vendor-category']){
                manufacturer.tags['vendor-category'].forEach((tag, tagIndex) => {
                    productTypeArray.push({
                        "@type": "ListItem",
                        position: tagIndex + 1,
                        name: tag.name,
                    });
                });
            }

            let organizationItem = {
                "@type": "Organization",
                name: manufacturerName,
                url: route(
                    "web.catalog.manufacturers.show",
                    manufacturer.slug
                ),
                mainEntityOfPage: {
                    "@type": "WebPage",
                    "@id": route(
                        "web.catalog.manufacturers.show",
                        manufacturer.slug
                    ),
                },
            };

            // Only add logo if it has a valid external_id
            if (manufacturer.logo?.external_id) {
                organizationItem.logo = marketingAssetUrl(manufacturer.logo.external_id);
            }

            // Only add hasOfferCatalog if there are product categories
            if (productTypeArray.length > 0) {
                organizationItem.hasOfferCatalog = {
                    "@type": "OfferCatalog",
                    name: "Product Categories",
                    description:
                        "A list of product categories supplied by " +
                        manufacturerName,
                    itemListElement: productTypeArray,
                };
            }

            itemListElement.push({
                "@type": "ListItem",
                position: index + 1,
                item: organizationItem,
            });
        });

        return {
            "@type": "CollectionPage",
            "@id": route(route().current(), route().params),
            name: "Manufacturers",
            description:
                "A collection of manufacturers who supply products to the site.",
            url: route(route().current(), route().params),
            mainEntity: {
                "@type": "ItemList",
                name: "Manufacturers List",
                description:
                    "A collection of manufacturers who supply products to the site.",
                numberOfItems: totalCount,
                itemListElement: itemListElement,
            },
        };
    };

    const jsonLdMainEntityManufacturer = (manufacturer, series) => {
        let seriesData = [];

        series.forEach((serie, index) => {
            seriesData.push({
                "@type": "ListItem",
                position: index + 1,
                name: serie.name,
            });
        });

        let mainEntity = {
            "@type": "WebPage",
            "@id": route(route().current(), route().params),
            name: manufacturer.name,
            description:
                manufacturer.name +
                " is a manufacturer that supplies product to SECLOCK.",
            url: route(route().current(), route().params),
        };

        if (seriesData.length) {
            // mainEntity.hasPart = {
            //     "@type": "ItemList",
            //     name:
            //         "Page 1" +
            //         series.meta.current_page +
            //         " of " +
            //         series.meta.last_page +
            //         "1 pages of Series offered by " +
            //         manufacturer.display_name,
            //     numberOfItems: series.meta.total,
            //     itemListElement: seriesData,
            //     prev: series.meta.prev,
            //     next: series.meta.next,
            // };
        }

        return mainEntity;
    };

    const jsonLdMainEntityWebPage = (title, description) => {
        return {
            "@type": "WebPage",
            "@id": route(route().current(), route().params),
            name: title,
            description: description,
            url: route(route().current(), route().params),
            audience: {
                "@type": "BusinessAudience",
                audienceType: "Businesses",
                description:
                    "This website caters to commercial door hardware, security, and access control professionals and organizations, spanning across multiple industries such as locksmith, contract hardware, glass and glazing, systems integrator, OEM, MRO, and online retail. Our platform features an extensive range of products from well-known brands like Schlage, Von Duprin, LCN, HES, Adams Rite, Corbin Russwin, Sargent, Yale, Norton, and Dormakaba.",
            },
        };
    };

    const jsonLdMainEntityContactPage = (title, description) => {
        return {
            "@type": "ContactPage",
            "@id": route(route().current(), route().params),
            name: title,
            description: description,
            url: route(route().current(), route().params),
        };
    };

    const jsonLdMainEntityLocations = (locations) => {
        const localBusinessList = locations
            .filter(location => !location.under_construction)
            .map((location, index) => ({
                "@type": "LocalBusiness",
                "@id": `https://www.seclock.com/locations#${location.state.toLowerCase()}`,
                name: `SECLOCK - ${location.city}, ${location.state}`,
                description: `SECLOCK commercial door hardware distribution center in ${location.city}, ${location.state}`,
                address: {
                    "@type": "PostalAddress",
                    streetAddress: location.address,
                    addressLocality: location.city,
                    addressRegion: location.state,
                    postalCode: location.zip,
                    addressCountry: "US"
                },
                geo: location.map?.markers?.[0] ? {
                    "@type": "GeoCoordinates",
                    latitude: location.map.markers[0].lat,
                    longitude: location.map.markers[0].lng
                } : undefined,
                telephone: location.phone || undefined,
                openingHoursSpecification: location.hours ? {
                    "@type": "OpeningHoursSpecification",
                    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                    description: location.hours
                } : undefined,
                parentOrganization: {
                    "@type": "Organization",
                    "@id": "https://www.seclock.com/#organization"
                }
            }));

        return {
            "@type": "WebPage",
            "@id": route(route().current(), route().params),
            name: "SECLOCK Locations",
            description: "Find SECLOCK commercial door hardware distributor locations across the United States.",
            url: route(route().current(), route().params),
            mainEntity: {
                "@type": "ItemList",
                name: "SECLOCK Distribution Centers",
                numberOfItems: localBusinessList.length,
                itemListElement: localBusinessList.map((business, index) => ({
                    "@type": "ListItem",
                    position: index + 1,
                    item: business
                }))
            }
        };
    };

    const jsonLdMainEntityPriceBooks = (manufacturers) => {
        const maxItems = 20;
        let itemListElement = [];
        const totalCount = manufacturers?.data?.length || 0;

        manufacturers?.data?.slice(0, maxItems).forEach((manufacturer, index) => {
            itemListElement.push({
                "@type": "ListItem",
                position: index + 1,
                item: {
                    "@type": "DigitalDocument",
                    name: manufacturer.name + " Price Book",
                    description: "Price book and catalog for " + manufacturer.name + " products.",
                    url: route("web.catalog.price-books.show", manufacturer.slug),
                    provider: {
                        "@type": "Organization",
                        name: manufacturer.name,
                    },
                },
            });
        });

        return {
            "@type": "CollectionPage",
            "@id": route(route().current(), route().params),
            name: "Price Books",
            description: "A collection of price books and catalogs from commercial door hardware manufacturers.",
            url: route(route().current(), route().params),
            mainEntity: {
                "@type": "ItemList",
                name: "Price Books List",
                description: "Available price books from manufacturers.",
                numberOfItems: totalCount,
                itemListElement: itemListElement,
            },
        };
    };

    const jsonLdMainEntityResources = (manufacturers) => {
        const maxItems = 20;
        let itemListElement = [];
        const totalCount = manufacturers?.length || 0;

        manufacturers?.slice(0, maxItems).forEach((manufacturer, index) => {
            itemListElement.push({
                "@type": "ListItem",
                position: index + 1,
                item: {
                    "@type": "WebPage",
                    name: manufacturer.display_name + " Resources",
                    url: route("web.catalog.resources.show", manufacturer.slug),
                },
            });
        });

        return {
            "@type": "CollectionPage",
            "@id": route(route().current(), route().params),
            name: "Resources",
            description: "A collection of technical resources, catalogs, and documentation from commercial door hardware manufacturers.",
            url: route(route().current(), route().params),
            mainEntity: {
                "@type": "ItemList",
                name: "Manufacturer Resources",
                description: "Technical resources organized by manufacturer.",
                numberOfItems: totalCount,
                itemListElement: itemListElement,
            },
        };
    };

    const jsonLdMainEntityResourcesShow = (manufacturer) => {
        const maxItems = 20;
        let hasPart = [];
        let totalCount = 0;

        // Resources are stored in manufacturer.resources as an object with category keys
        const resources = manufacturer?.resources || {};

        // Iterate over each category (e.g., "video", "catalog", "spec-sheet", etc.)
        Object.entries(resources).forEach(([category, resourceList]) => {
            if (Array.isArray(resourceList)) {
                resourceList.forEach((resource) => {
                    let resourceType = "TechArticle";
                    // Map category to appropriate schema type
                    if (category === "video" || category === "videos") {
                        resourceType = "VideoObject";
                    } else if (category === "catalog" || category === "catalogs" || category === "price-book") {
                        resourceType = "DigitalDocument";
                    }

                    hasPart.push({
                        "@type": resourceType,
                        name: resource.name || resource.title || "Resource",
                        description: resource.description || "Technical resource for " + manufacturer.name,
                        url: resource.url || resource.link,
                    });
                    totalCount++;
                });
            }
        });

        // Truncate to maxItems while keeping totalCount accurate
        const truncatedHasPart = hasPart.slice(0, maxItems);

        return {
            "@type": "CollectionPage",
            "@id": route(route().current(), route().params),
            name: manufacturer.name + " Resources",
            description: "Technical resources, catalogs, and documentation for " + manufacturer.name + " products.",
            url: route(route().current(), route().params),
            about: {
                "@type": "Brand",
                name: manufacturer.name,
            },
            mainEntity: {
                "@type": "ItemList",
                name: manufacturer.name + " Technical Resources",
                description: "Technical documentation and resources for " + manufacturer.name + ".",
                numberOfItems: totalCount,
                itemListElement: truncatedHasPart.map((item, index) => ({
                    "@type": "ListItem",
                    position: index + 1,
                    item: item,
                })),
            },
        };
    };

    const jsonLdMainEntityProductCategory = (categoryName, description, subcategories = []) => {
        let itemListElement = [];

        subcategories.forEach((subcategory, index) => {
            itemListElement.push({
                "@type": "ListItem",
                position: index + 1,
                item: {
                    "@type": "Product",
                    name: subcategory.name,
                    description: subcategory.description || subcategory.name + " products available at SECLOCK.",
                    url: subcategory.url,
                },
            });
        });

        let mainEntity = {
            "@type": "CollectionPage",
            "@id": route(route().current(), route().params),
            name: categoryName,
            description: description,
            url: route(route().current(), route().params),
        };

        if (itemListElement.length > 0) {
            mainEntity.mainEntity = {
                "@type": "ItemList",
                name: categoryName + " Products",
                description: "Product subcategories for " + categoryName + ".",
                numberOfItems: subcategories.length,
                itemListElement: itemListElement,
            };
        }

        return mainEntity;
    };

    const jsonLdMainEntityService = (name, description, features = []) => {
        let mainEntity = {
            "@type": "SoftwareApplication",
            "@id": route(route().current(), route().params),
            name: name,
            description: description,
            url: route(route().current(), route().params),
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web Browser",
            offers: {
                "@type": "Offer",
                price: "0",
                priceCurrency: "USD",
                availability: "https://schema.org/InStock",
            },
            provider: {
                "@type": "Organization",
                name: "SECLOCK",
                url: "https://www.seclock.com",
            },
        };

        if (features.length > 0) {
            mainEntity.featureList = features;
        }

        return mainEntity;
    };

    return {
        jsonLdMainEntityBlogIndex,
        jsonLdMainEntityBlogShow,
        jsonLdMainEntityContactPage,
        jsonLdMainEntityLocations,
        jsonLdMainEntityManufacturer,
        jsonLdMainEntityManufacturers,
        jsonLdMainEntityWebPage,
        jsonLdMainEntityPriceBooks,
        jsonLdMainEntityResources,
        jsonLdMainEntityResourcesShow,
        jsonLdMainEntityProductCategory,
        jsonLdMainEntityService,
    };
}
